import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { LOGO, BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-slate-900 p-14 text-white lg:flex">
        <div className="flex items-center gap-5">
          <img src={LOGO} alt={BRAND} className="h-24 w-24 rounded-full object-cover shadow-lg" />
          <div>
            <div className="font-display text-3xl font-black leading-none tracking-tight">{BRAND}</div>
            <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Google Ads P&amp;L Control
            </div>
          </div>
        </div>
        <div className="max-w-lg">
          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
            Every rupee of ad spend, every invoice, one desk.
          </h1>
          <p className="mt-6 text-sm text-slate-400">
            Merge your SharePoint daily reports, slice profit &amp; loss by via, campaign, client and
            manager, and track client validation plus payment status end to end.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 border-t border-slate-800 pt-8 text-xs uppercase tracking-[0.2em] text-slate-500">
          <div>Daily P&amp;L</div>
          <div>Client Validation</div>
          <div>Invoice Ledger</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-14">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <img
            src={LOGO}
            alt={BRAND}
            className="mb-5 h-24 w-24 rounded-full object-cover shadow-sm lg:hidden"
          />
          <h1 className="font-display text-3xl font-black tracking-tight lg:hidden">{BRAND}</h1>
          <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 lg:mt-0">
            Sign in
          </div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tight">Welcome back</h2>
          <div className="mt-8 space-y-5">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-md border-slate-300 focus:ring-slate-900"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 rounded-md border-slate-300 focus:ring-slate-900"
                required
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-md bg-slate-900 text-white transition-colors hover:bg-slate-800 active:scale-95"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
            <p className="text-xs text-slate-500">
              Trouble signing in? Ask your admin to check your access.
            </p>
            <button
              type="button"
              data-testid="forgot-password-link"
              className="text-xs font-semibold text-slate-900 underline-offset-4 hover:underline"
              onClick={() => setError("Ask your admin to reset your password from Team & Access.")}
            >
              Forgot password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
